import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Form, Nav, Tab, Spinner } from "react-bootstrap";
import axios from "axios";
import { Bar, Doughnut, Line, Pie } from "react-chartjs-2";
import "chart.js/auto";

//** Import Image */
import { fetchConnectedUser } from "../../../services/OrdersService";
import {
  fetchMenusByRestaurant,
  fetchRestoDataByUser,
} from "../../../services/restaurantService";
import { fetchCategories, fetchProducts } from "../../../services/MenuService";
import { fetchActiveSubCategories } from "../../../services/subcategories";
import config from "../../../../config.json";

import "../../../i18n";
import i18n from "../../../i18n";
import { useTranslation } from "react-i18next";
import { getDashboardResetSince } from "../../../utils/dashboardArchiveStorage";
import { getMenuLanguageFlag } from "../../../constants/menuLanguages";
import swal from "sweetalert";
import DashboardOpsCharts from "./DashboardOpsCharts";
import {
  LuCalendarDays,
  LuChartArea,
  LuChartBar,
  LuChartPie,
  LuDatabase,
  LuFolderTree,
  LuHand,
  LuLanguages,
  LuListChecks,
  LuMessageSquare,
  LuMousePointerClick,
} from "react-icons/lu";

const CLAIM_CATEGORY_DOUGHNUT_COLORS = [
  "#5b8def",
  "#22c55e",
  "#f59e0b",
  "#a855f7",
  "#ec4899",
  "#14b8a6",
  "#f97316",
  "#64748b",
];

/** Reclamation status donut — softer chart tones: orange pending, golden yellow in progress, green resolved */
const RECLAMATION_STATUS_DONUT_COLORS = {
  pending: "#E8894A",
  in_progress: "#D9B84C",
  resolved: "#4CAF7C",
};

const CLAIMS_STATUS_AREA_ORDER = ["resolved", "in_progress", "pending"];
/** X-axis label order (matches backend ALLOWED_RECLAMATION_STATUSES) */
const CLAIMS_STATUS_AXIS_KEYS = ["pending", "in_progress", "resolved"];

const MENU_LANGUAGE_RANK_COLORS = [
  "#4A90E2", // blue
  "#2EC27E", // green
  "#F3C623", // yellow
  "#A855F7", // purple
  "#64748B", // slate
  "#F97316", // orange
  "#14B8A6", // teal
  "#EC4899", // pink
];

function hexToRgba(hex, alpha) {
  const h = (hex || "#888888").replace("#", "");
  if (h.length !== 6) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const menuLanguageFlagImageCache = new Map();

function loadMenuLanguageFlagImage(src) {
  if (!src) return null;
  if (!menuLanguageFlagImageCache.has(src)) {
    const img = new Image();
    img.src = src;
    menuLanguageFlagImageCache.set(src, img);
  }
  return menuLanguageFlagImageCache.get(src);
}

const menuLanguageBarFlagPlugin = {
  id: "menuLanguageBarFlagPlugin",
  afterDraw(chart) {
    const items = chart.options?.plugins?.menuLanguageBarLabels?.items || [];
    const yScale = chart.scales?.y;
    if (!yScale || !items.length) return;
    const { ctx } = chart;
    const flagW = 18;
    const flagH = 13;
    const gap = 6;
    const startX = 4;

    ctx.save();
    ctx.font = "600 12px Poppins, sans-serif";
    ctx.fillStyle = "#334155";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";

    items.forEach((item, index) => {
      const y = yScale.getPixelForValue(index);
      const label = item?.label || "";
      const flagX = startX;
      const textX = startX + flagW + gap;

      const img = loadMenuLanguageFlagImage(item?.flag);
      if (img?.complete && img.naturalWidth) {
        ctx.save();
        ctx.beginPath();
        ctx.roundRect(flagX, y - flagH / 2, flagW, flagH, 2);
        ctx.clip();
        ctx.drawImage(img, flagX, y - flagH / 2, flagW, flagH);
        ctx.restore();
      }

      ctx.fillText(label, textX, y);
    });
    ctx.restore();
  },
};

function MenuLanguageFlag({ src, alt = "", className = "menu-language-flag-icon" }) {
  if (!src) {
    return <span className={`${className} menu-language-flag-fallback`} aria-hidden />;
  }
  return <img src={src} alt={alt} className={className} loading="lazy" />;
}

/** Category chart: report range (dropdown) */
const CLAIMS_CATEGORY_WINDOW_OPTIONS = [7, 14, 30, 60, 90, 180, 365];


function formatShortDate(date, locale = "en") {
  return date.toLocaleDateString(locale, { day: "2-digit", month: "short" });
}

function getShortMonthLabels(locale = "en") {
  return Array.from({ length: 12 }, (_, i) =>
    new Intl.DateTimeFormat(locale, { month: "short" }).format(new Date(2020, i, 1))
  );
}

function getCategoryDisplayName(category) {
  const details = Array.isArray(category?.categoryDetails)
    ? category.categoryDetails
    : [];
  const preferred =
    details.find((d) => d?.language === "en") ||
    details.find((d) => d?.language === "fr") ||
    details[0] ||
    {};

  return (
    preferred?.categoryTitle ||
    preferred?.categoryName ||
    preferred?.title ||
    category?.categoryTitle ||
    category?.name ||
    category?.title ||
    category?.slug ||
    ""
  );
}

function getProductDisplayName(product) {
  const details = Array.isArray(product?.productDetails) ? product.productDetails : [];
  const preferred =
    details.find((d) => d?.language === "en") ||
    details.find((d) => d?.language === "fr") ||
    details[0] ||
    {};

  return (
    preferred?.productTitle ||
    preferred?.productName ||
    preferred?.title ||
    product?.productTitle ||
    product?.productName ||
    product?.name ||
    product?.title ||
    ""
  );
}

function extractId(value) {
  if (!value) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object") {
    const raw =
      value?._id ??
      value?.id ??
      value?.productId ??
      value?.productFK ??
      value?.value ??
      "";
    if (!raw) return "";
    if (typeof raw === "string" || typeof raw === "number") return String(raw);
    if (typeof raw === "object") {
      if (raw?.$oid) return String(raw.$oid);
      if (typeof raw.toString === "function") return String(raw.toString());
    }
  }
  return "";
}


function Home() {
  const { t } = useTranslation();
  const [totalMenus, setTotalMenus] = useState(0);
  const [imageStorageUsage, setImageStorageUsage] = useState("0 B / 500 MB");
  const [imageStorageBytes, setImageStorageBytes] = useState(0);
  const [openStats, setOpenStats] = useState({
    today: [],
    weekly: [],
    monthly: [],
  });
  const [entityCounts, setEntityCounts] = useState({
    categories: 0,
    products: 0,
    items: 0,
    choices: 0,
    subcategories: 0,
  });

  const [dashboardScopeId, setDashboardScopeId] = useState(null);
  const [dashboardResetSinceISO, setDashboardResetSinceISO] = useState(null);
  const [dbStatsFilter, setDbStatsFilter] = useState("all");
  const [dbChartType, setDbChartType] = useState("bar");
  const [googleReviewClickStats, setGoogleReviewClickStats] = useState({
    labels: [],
    counts: [],
    total: 0,
  });
  const [googleReviewDays, setGoogleReviewDays] = useState(30);
  const [googleReviewChartKind, setGoogleReviewChartKind] = useState("line");
  const [googleReviewLoading, setGoogleReviewLoading] = useState(false);
  const [menuLanguageClickStats, setMenuLanguageClickStats] = useState({
    series: [],
    total: 0,
  });
  const [menuLanguageClickLoading, setMenuLanguageClickLoading] = useState(false);
  const [menuLanguageViewKind, setMenuLanguageViewKind] = useState("list");
  const [menuLanguageFlagEpoch, setMenuLanguageFlagEpoch] = useState(0);
  /** Calendar filter for language clicks API (matches revenue pills: All / Jan–Dec) */
  const [selectedMenuLanguageMonth, setSelectedMenuLanguageMonth] = useState("all");
  const [claimsByCategoryStats, setClaimsByCategoryStats] = useState({
    series: [],
  });
  const [claimsCategoryDays, setClaimsCategoryDays] = useState(30);
  const [claimsCategoryChartKind, setClaimsCategoryChartKind] = useState("pie");
  const [claimsCategoryLoading, setClaimsCategoryLoading] = useState(false);
  const [claimsByStatusStats, setClaimsByStatusStats] = useState({ series: [] });
  const [claimsStatusLoading, setClaimsStatusLoading] = useState(false);
  const [claimsStatusChartKind, setClaimsStatusChartKind] = useState("area");
  const [claimsStatusAreaVisible, setClaimsStatusAreaVisible] = useState({
    pending: true,
    in_progress: true,
    resolved: true,
  });

  const toggleClaimsStatusAreaFilter = useCallback((statusKey) => {
    setClaimsStatusAreaVisible((prev) => {
      const onCount = CLAIMS_STATUS_AXIS_KEYS.filter((k) => prev[k]).length;
      if (prev[statusKey] && onCount <= 1) return prev;
      return { ...prev, [statusKey]: !prev[statusKey] };
    });
  }, []);

  const loadGoogleReviewClicks = useCallback(async () => {
    setGoogleReviewLoading(true);
    try {
      const token = localStorage.getItem("tokenLogin");
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${config.dev}/v2/restaurant/google-review-click-stats`,
        {
          headers,
          params: { days: googleReviewDays },
        }
      );
      const series = Array.isArray(res.data?.series) ? res.data.series : [];
      const labels = series.map((s) =>
        formatShortDate(new Date(`${s.day}T12:00:00.000Z`), i18n.language)
      );
      const counts = series.map((s) => Number(s.count) || 0);
      setGoogleReviewClickStats({
        labels,
        counts,
        total: Number(res.data?.total) || 0,
      });
    } catch (err) {
      console.error("Error loading Google review click stats:", err);
      setGoogleReviewClickStats({ labels: [], counts: [], total: 0 });
    } finally {
      setGoogleReviewLoading(false);
    }
  }, [googleReviewDays, i18n.language]);

  const loadMenuLanguageClickStats = useCallback(async () => {
    setMenuLanguageClickLoading(true);
    try {
      const token = localStorage.getItem("tokenLogin");
      const headers = { Authorization: `Bearer ${token}` };
      const calendarYear = new Date().getFullYear();
      const res = await axios.get(`${config.dev}/v2/restaurant/language-click-stats`, {
        headers,
        params:
          selectedMenuLanguageMonth === "all"
            ? { month: "all", year: calendarYear }
            : { month: selectedMenuLanguageMonth },
      });
      const series = Array.isArray(res.data?.series) ? res.data.series : [];
      setMenuLanguageClickStats({
        series,
        total: Number(res.data?.total) || 0,
      });
    } catch (err) {
      console.error("Error loading menu language click stats:", err);
      setMenuLanguageClickStats({ series: [], total: 0 });
    } finally {
      setMenuLanguageClickLoading(false);
    }
  }, [selectedMenuLanguageMonth]);

  const loadClaimsByCategoryStats = useCallback(async () => {
    setClaimsCategoryLoading(true);
    try {
      const token = localStorage.getItem("tokenLogin");
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${config.dev}/client-reclamation/stats/by-type`, {
        headers,
        params: { days: claimsCategoryDays },
      });
      const series = Array.isArray(res.data?.series) ? res.data.series : [];
      setClaimsByCategoryStats({ series });
    } catch (err) {
      console.error("Error loading claims by category:", err);
      setClaimsByCategoryStats({ series: [] });
    } finally {
      setClaimsCategoryLoading(false);
    }
  }, [claimsCategoryDays]);

  const loadClaimsByStatusStats = useCallback(async () => {
    setClaimsStatusLoading(true);
    try {
      const token = localStorage.getItem("tokenLogin");
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${config.dev}/client-reclamation/stats/by-status`, {
        headers,
        params: { days: claimsCategoryDays },
      });
      const series = Array.isArray(res.data?.series) ? res.data.series : [];
      setClaimsByStatusStats({ series });
    } catch (err) {
      console.error("Error loading claims by status:", err);
      setClaimsByStatusStats({ series: [] });
    } finally {
      setClaimsStatusLoading(false);
    }
  }, [claimsCategoryDays]);

  const loadDashboard = useCallback(async () => {
    let scopeId = "";
    try {
      const user = await fetchConnectedUser();
      scopeId = String(user?.restaurantFK || user?._id || "");
    } catch {
      scopeId = "";
    }
    if (!scopeId) scopeId = "anonymous";
    setDashboardScopeId(scopeId);
    const resetSince = getDashboardResetSince(scopeId);
    setDashboardResetSinceISO(resetSince);

    const token = localStorage.getItem("tokenLogin");
    const headers = { Authorization: `Bearer ${token}` };
    const sinceQ = resetSince ? { since: resetSince } : {};

    const loadMenus = async () => {
      try {
        const dataMenu = await fetchMenusByRestaurant();
        const activeEnabledMenus = (Array.isArray(dataMenu) ? dataMenu : []).filter(
          (menu) => menu?.isEnabled === true && menu?.isArchived !== true
        );
        setTotalMenus(activeEnabledMenus.length);
      } catch (err) {
        console.error("Erreur de chargement des menus :", err);
      }
    };

    const loadEntities = async () => {
      try {
        const [categories, products, subcategories, itemsRes, choicesRes] =
          await Promise.all([
            fetchCategories(),
            fetchProducts(),
            fetchActiveSubCategories(),
            axios.get(`${config.dev}/v2/items/retrieve`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${config.dev}/v2/choices/retrieve`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

        const items = Array.isArray(itemsRes?.data)
          ? itemsRes.data.filter((x) => !x?.isArchived)
          : [];
        const choices = Array.isArray(choicesRes?.data)
          ? choicesRes.data.filter((x) => !x?.isArchived)
          : [];

        setEntityCounts({
          categories: Array.isArray(categories) ? categories.length : 0,
          products: Array.isArray(products) ? products.length : 0,
          items: items.length,
          choices: choices.length,
          subcategories: Array.isArray(subcategories) ? subcategories.length : 0,
        });
      } catch (err) {
        console.error("Erreur de chargement des stats entités :", err);
      }
    };

    const loadOpenStats = async () => {
      try {
        const [todayRes, weeklyRes, monthlyRes] = await Promise.all([
          axios.get(`${config.dev}/v2/products/open-stats`, {
            headers,
            params: { period: "today", ...sinceQ },
          }),
          axios.get(`${config.dev}/v2/products/open-stats`, {
            headers,
            params: { period: "weekly", ...sinceQ },
          }),
          axios.get(`${config.dev}/v2/products/open-stats`, {
            headers,
            params: { period: "monthly", ...sinceQ },
          }),
        ]);

        setOpenStats({
          today: todayRes?.data?.topProducts || [],
          weekly: weeklyRes?.data?.topProducts || [],
          monthly: monthlyRes?.data?.topProducts || [],
        });
      } catch (err) {
        console.error("Error loading product open stats:", err);
      }
    };

    const loadRestaurantStorage = async () => {
      try {
        const restoData = await fetchRestoDataByUser();
        setImageStorageUsage(
          restoData?.restaurant?.imageStorageUsageDisplay || "0 B / 500 MB"
        );
        setImageStorageBytes(
          Number(restoData?.restaurant?.totalImageUsageBytes || 0)
        );
      } catch (err) {
        console.error("Error loading restaurant storage usage:", err);
      }
    };

    await Promise.all([
      loadMenus(),
      loadEntities(),
      loadOpenStats(),
      loadRestaurantStorage(),
    ]);
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    loadGoogleReviewClicks();
  }, [loadGoogleReviewClicks]);

  useEffect(() => {
    loadClaimsByCategoryStats();
  }, [loadClaimsByCategoryStats]);

  useEffect(() => {
    loadClaimsByStatusStats();
  }, [loadClaimsByStatusStats]);

  useEffect(() => {
    loadMenuLanguageClickStats();
  }, [loadMenuLanguageClickStats]);

  const dbStatsData = [
    { key: "categories", label: t("dashboardEntityCategories"), value: entityCounts.categories },
    { key: "subcategories", label: t("dashboardEntitySubcategories"), value: entityCounts.subcategories },
    { key: "products", label: t("dashboardEntityProducts"), value: entityCounts.products },
    { key: "choices", label: t("dashboardEntityChoices"), value: entityCounts.choices },
    { key: "items", label: t("dashboardEntityItems"), value: entityCounts.items },
  ];
  const dbFilteredData = useMemo(() => {
    if (dbStatsFilter === "menu") {
      return dbStatsData.filter((x) =>
        ["categories", "subcategories", "products"].includes(x.key)
      );
    }
    if (dbStatsFilter === "options") {
      return dbStatsData.filter((x) => ["choices", "items"].includes(x.key));
    }
    return dbStatsData;
  }, [dbStatsData, dbStatsFilter]);

  const dbStatsMax = Math.max(...dbFilteredData.map((x) => x.value), 1);

  const dbStatsTotal = dbFilteredData.reduce((sum, x) => sum + x.value, 0);
  const maxStorageInBytes = 500 * 1024 * 1024;
  const imageStoragePercent = Math.min(
    100,
    Math.max(0, (imageStorageBytes / maxStorageInBytes) * 100)
  );
  const dbChartData = {
    labels: dbFilteredData.map((x) => x.label),
    datasets: [
      {
        type: "bar",
        label: t("dashboardDbChartCount"),
        data: dbFilteredData.map((x) => x.value),
        backgroundColor: ["#5B8DEF", "#7A6FF0", "#52C7B8", "#F5A65B", "#E97AA8"],
        borderRadius: 6,
        barThickness: 32,
        clip: false,
        yAxisID: "y",
      },
      {
        type: "line",
        label: t("dashboardDbChartSharePct"),
        data: dbFilteredData.map((x) =>
          dbStatsTotal ? Math.round((x.value / dbStatsTotal) * 100) : 0
        ),
        borderColor: "#e67c30",
        backgroundColor: "#e67c30",
        tension: 0.35,
        pointRadius: 4,
        pointHoverRadius: 5,
        yAxisID: "y1",
      },
    ],
  };
  const dbDonutData = {
    labels: dbFilteredData.map((x) => x.label),
    datasets: [
      {
        data: dbFilteredData.map((x) => x.value),
        backgroundColor: ["#4e73be", "#7c93d6", "#a4b4e5", "#f2b36f", "#9dd5a6"],
        borderWidth: 0,
      },
    ],
  };
  const dbDonutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
    },
    cutout: "68%",
  };
  const dbChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20 },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        suggestedMax: Math.max(dbStatsMax + 2, 5),
        ticks: { precision: 0 },
      },
      y1: {
        display: false,
        beginAtZero: true,
        position: "right",
        suggestedMax: 100,
        grid: { drawOnChartArea: false },
      },
    },
  };
  const barValueLabelPlugin = {
    id: "barValueLabelPlugin",
    afterDatasetsDraw: (chart) => {
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const values = chart.data.datasets?.[0]?.data || [];
      ctx.save();
      ctx.fillStyle = "#0f172a";
      ctx.font = "600 11px Poppins, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "bottom";
      meta.data.forEach((bar, index) => {
        if (!bar) return;
        const value = values[index];
        if (value == null || value === "") return;
        const num = Number(value);
        if (!Number.isFinite(num) || num <= 0) return;
        ctx.fillText(String(num), bar.x, bar.y - 6);
      });
      ctx.restore();
    },
  };

  /** Value labels to the right of each bar (indexAxis: "y"). */
  const horizontalBarValueLabelPlugin = {
    id: "horizontalBarValueLabelPlugin",
    afterDatasetsDraw: (chart) => {
      if (chart.options?.indexAxis !== "y") return;
      const { ctx } = chart;
      const meta = chart.getDatasetMeta(0);
      const values = chart.data.datasets?.[0]?.data || [];
      ctx.save();
      ctx.fillStyle = "#0f172a";
      ctx.font = "600 11px Poppins, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      meta.data.forEach((bar, index) => {
        if (!bar) return;
        const value = values[index];
        if (value == null || value === "") return;
        const num = Number(value);
        if (!Number.isFinite(num) || num <= 0) return;
        const endX = Math.max(bar.x ?? 0, bar.base ?? 0);
        ctx.fillText(String(num), endX + 6, bar.y);
      });
      ctx.restore();
    },
  };

  const googleReviewMax = Math.max(0, ...googleReviewClickStats.counts, 1);
  const googleReviewInsights = useMemo(() => {
    const { counts, labels, total } = googleReviewClickStats;
    if (!counts.length) {
      return { avg: 0, peak: 0, peakLabel: "—" };
    }
    let peakIdx = 0;
    counts.forEach((v, i) => {
      if (v > counts[peakIdx]) peakIdx = i;
    });
    return {
      avg: total / Math.max(googleReviewDays, 1),
      peak: counts[peakIdx],
      peakLabel: labels[peakIdx] || "—",
    };
  }, [googleReviewClickStats, googleReviewDays]);

  const googleReviewChartData = useMemo(() => {
    const counts = googleReviewClickStats.counts;
    const n = counts.length;
    const barFill = "rgba(91, 141, 239, 0.72)";
    const barStroke = "rgba(91, 141, 239, 0.95)";
    return {
      labels: googleReviewClickStats.labels,
      datasets: [
        {
          label: t("googleReviewClicksSeriesLabel"),
          data: counts,
          backgroundColor:
            n > 0 ? Array.from({ length: n }, () => barFill) : barFill,
          borderColor:
            n > 0 ? Array.from({ length: n }, () => barStroke) : barStroke,
          borderWidth: 1,
          borderRadius: 10,
          borderSkipped: false,
          maxBarThickness: 22,
        },
      ],
    };
  }, [googleReviewClickStats, t]);
  const googleReviewChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 20, left: 4, right: 8, bottom: 4 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
          titleFont: { size: 13, weight: "600" },
          bodyFont: { size: 13 },
        },
      },
      scales: {
        x: {
          grid: { display: false, drawBorder: false },
          ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 16 },
        },
        y: {
          beginAtZero: true,
          suggestedMax: Math.max(googleReviewMax + 1, 4),
          ticks: { precision: 0 },
          grid: { color: "rgba(15, 23, 42, 0.06)" },
          border: { display: false },
        },
      },
    }),
    [googleReviewMax]
  );

  const googleReviewLineChartData = useMemo(
    () => ({
      labels: googleReviewClickStats.labels,
      datasets: [
        {
          label: t("googleReviewClicksSeriesLabel"),
          data: googleReviewClickStats.counts,
          borderColor: "#5b8def",
          backgroundColor: "rgba(91, 141, 239, 0.14)",
          fill: true,
          tension: 0.38,
          borderWidth: 2.5,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: "#5b8def",
          pointBorderWidth: 2,
        },
      ],
    }),
    [googleReviewClickStats, t]
  );
  const googleReviewLineChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: "index" },
      layout: { padding: { top: 16, left: 4, right: 8, bottom: 4 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
        },
      },
      scales: {
        x: {
          grid: { display: false, drawBorder: false },
          ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 16 },
        },
        y: {
          beginAtZero: true,
          suggestedMax: Math.max(googleReviewMax + 1, 4),
          ticks: { precision: 0 },
          grid: { color: "rgba(15, 23, 42, 0.06)" },
          border: { display: false },
        },
      },
    }),
    [googleReviewMax]
  );

  const menuLanguageRankedSeries = useMemo(() => {
    const rows = Array.isArray(menuLanguageClickStats.series)
      ? menuLanguageClickStats.series
      : [];
    const toLabel = (code) => {
      try {
        return new Intl.DisplayNames([i18n.language], { type: "language" }).of(code) || code;
      } catch {
        return code;
      }
    };
    return rows.map((r, i) => ({
      language: String(r.language || "").toLowerCase(),
      label: toLabel(String(r.language || "").toLowerCase()),
      flag: getMenuLanguageFlag(String(r.language || "").toLowerCase()),
      count: Number(r.count) || 0,
      color: MENU_LANGUAGE_RANK_COLORS[i % MENU_LANGUAGE_RANK_COLORS.length],
    }));
  }, [menuLanguageClickStats, i18n.language]);

  const menuLanguageRankMax = useMemo(() => {
    const values = menuLanguageRankedSeries.map((x) => x.count);
    return Math.max(0, ...values, 1);
  }, [menuLanguageRankedSeries]);

  const menuLanguageRankData = useMemo(() => {
    const hasData = menuLanguageRankedSeries.length > 0;
    const rows = hasData
      ? menuLanguageRankedSeries
      : [{ label: t("menuLanguageClicksNone"), count: 1 }];
    return {
      labels: rows.map((x) => x.label),
      datasets: [
        {
          label: t("menuLanguageClicksSeriesLabel"),
          data: rows.map((x) => x.count),
          backgroundColor: rows.map((x) =>
            hexToRgba(x.color || MENU_LANGUAGE_RANK_COLORS[0], 0.38)
          ),
          borderColor: rows.map((x) =>
            hexToRgba(x.color || MENU_LANGUAGE_RANK_COLORS[0], 0.38)
          ),
          borderWidth: 1,
          borderRadius: 10,
          borderSkipped: false,
          maxBarThickness: 36,
        },
      ],
    };
  }, [menuLanguageRankedSeries, t]);

  const menuLanguageBarLeftPadding = useMemo(() => {
    if (typeof document === "undefined") return 120;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return 120;
    ctx.font = "600 12px Poppins, sans-serif";
    const maxLabelW = menuLanguageRankedSeries.reduce((max, row) => {
      const w = ctx.measureText(row.label || "").width;
      return Math.max(max, w);
    }, 0);
    return Math.ceil(maxLabelW) + 18 + 6 + 20;
  }, [menuLanguageRankedSeries]);

  const menuLanguageRankOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 10,
          left: menuLanguageBarLeftPadding,
          right: 24,
          bottom: 0,
        },
      },
      plugins: {
        legend: { display: false },
        menuLanguageBarLabels: {
          items: menuLanguageRankedSeries.map((row) => ({
            flag: row.flag,
            label: row.label,
          })),
        },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
          callbacks: {
            label: (ctx) => {
              const value = Number(ctx.raw) || 0;
              const pct = menuLanguageClickStats.total
                ? Math.round((value / menuLanguageClickStats.total) * 100)
                : 0;
              return `${value} (${pct}%)`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          suggestedMax: Math.max(menuLanguageRankMax + 1, 4),
          ticks: { precision: 0, color: "#64748b" },
          grid: { color: "rgba(15, 23, 42, 0.06)" },
          border: { display: false },
        },
        y: {
          grid: { display: false },
          ticks: { display: false },
          border: { display: false },
        },
      },
    }),
    [
      menuLanguageRankMax,
      menuLanguageClickStats.total,
      menuLanguageRankedSeries,
      menuLanguageBarLeftPadding,
    ]
  );

  const menuLanguagePieData = useMemo(() => {
    const rows = menuLanguageRankedSeries.filter((row) => row.count > 0);
    const hasData = rows.length > 0;
    return {
      labels: hasData ? rows.map((row) => row.label) : [""],
      datasets: [
        {
          data: hasData ? rows.map((row) => row.count) : [1],
          backgroundColor: hasData
            ? rows.map((row) => row.color || MENU_LANGUAGE_RANK_COLORS[0])
            : ["#e2e8f0"],
          borderWidth: 2,
          borderColor: "#ffffff",
        },
      ],
    };
  }, [menuLanguageRankedSeries]);

  const menuLanguagePieBreakdown = useMemo(() => {
    const rows = menuLanguageRankedSeries.filter((row) => row.count > 0);
    const total = menuLanguageClickStats.total;
    return rows.map((row) => {
      const value = row.count;
      return {
        key: row.language,
        label: row.label,
        flag: row.flag,
        value,
        color: row.color,
        pct: total > 0 ? Math.round((value / total) * 100) : 0,
      };
    });
  }, [menuLanguageRankedSeries, menuLanguageClickStats.total]);

  const menuLanguagePieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
          callbacks: {
            label: (ctx) => {
              const value = Number(ctx.raw) || 0;
              const pct = menuLanguageClickStats.total
                ? Math.round((value / menuLanguageClickStats.total) * 100)
                : 0;
              return `${value} (${pct}%)`;
            },
          },
        },
      },
    }),
    [menuLanguageClickStats.total]
  );

  useEffect(() => {
    let cancelled = false;
    const urls = [
      ...new Set(
        menuLanguageRankedSeries.map((row) => row.flag).filter(Boolean)
      ),
    ];
    if (!urls.length) return undefined;
    Promise.all(
      urls.map(
        (src) =>
          new Promise((resolve) => {
            const img = loadMenuLanguageFlagImage(src);
            if (img?.complete) {
              resolve();
              return;
            }
            img.onload = () => resolve();
            img.onerror = () => resolve();
          })
      )
    ).then(() => {
      if (!cancelled) setMenuLanguageFlagEpoch((n) => n + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [menuLanguageRankedSeries]);

  const claimsCategoryTotal = useMemo(() => {
    const s = claimsByCategoryStats.series || [];
    return s.reduce((acc, x) => acc + (Number(x.count) || 0), 0);
  }, [claimsByCategoryStats]);

  const claimsCategoryHorizontalBarMax = useMemo(() => {
    const counts = (claimsByCategoryStats.series || []).map((x) => Number(x.count) || 0);
    return Math.max(0, ...counts, 1);
  }, [claimsByCategoryStats]);

  const claimsCategoryHorizontalBarData = useMemo(() => {
    const series = claimsByCategoryStats.series || [];
    const n = series.length;
    const counts = series.map((s) => Number(s.count) || 0);
    const fills = series.map(
      (_, i) => CLAIM_CATEGORY_DOUGHNUT_COLORS[i % CLAIM_CATEGORY_DOUGHNUT_COLORS.length]
    );
    return {
      labels: series.map((s) => t(`claimCategory_${s.type}`)),
      datasets: [
        {
          label: t("claimsByCategorySeriesLabel"),
          data: counts,
          backgroundColor: n > 0 ? fills : CLAIM_CATEGORY_DOUGHNUT_COLORS[0],
          borderColor: n > 0 ? fills.map(() => "#ffffff") : "#ffffff",
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false,
          maxBarThickness: 22,
        },
      ],
    };
  }, [claimsByCategoryStats, t]);

  const claimsCategoryHorizontalBarOptions = useMemo(
    () => ({
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 12, left: 4, right: 28, bottom: 8 } },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          suggestedMax: Math.max(claimsCategoryHorizontalBarMax + 1, 4),
          ticks: { precision: 0, color: "#64748b" },
          grid: {
            color: "rgba(15, 23, 42, 0.08)",
            drawBorder: true,
            borderColor: "rgba(15, 23, 42, 0.12)",
          },
          border: { display: true, color: "rgba(15, 23, 42, 0.12)" },
        },
        y: {
          grid: { display: false, drawBorder: true, borderColor: "rgba(15, 23, 42, 0.12)" },
          ticks: {
            color: "#334155",
            font: { size: 11, weight: "500" },
            autoSkip: false,
          },
          border: { display: true, color: "rgba(15, 23, 42, 0.12)" },
        },
      },
    }),
    [claimsCategoryHorizontalBarMax]
  );

  const claimsCategoryBarChartMinHeight = useMemo(() => {
    const n = (claimsByCategoryStats.series || []).length || 8;
    return Math.max(260, n * 34 + 80);
  }, [claimsByCategoryStats]);

  const claimsCategoryPieData = useMemo(() => {
    const series = claimsByCategoryStats.series || [];
    const filtered = series.filter((s) => (Number(s.count) || 0) > 0);
    const hasData = filtered.length > 0;
    return {
      labels: hasData ? filtered.map((s) => t(`claimCategory_${s.type}`)) : [""],
      datasets: [
        {
          data: hasData ? filtered.map((s) => Number(s.count) || 0) : [1],
          backgroundColor: hasData
            ? filtered.map(
                (_, i) =>
                  CLAIM_CATEGORY_DOUGHNUT_COLORS[i % CLAIM_CATEGORY_DOUGHNUT_COLORS.length]
              )
            : ["#e2e8f0"],
          borderWidth: 2,
          borderColor: "#ffffff",
        },
      ],
    };
  }, [claimsByCategoryStats, t]);

  /** Legend + bar stripes beside pie (same pattern as Orders by status `txBreakdown`) */
  const claimsCategoryPieBreakdown = useMemo(() => {
    const series = claimsByCategoryStats.series || [];
    const filtered = series.filter((s) => (Number(s.count) || 0) > 0);
    const total = claimsCategoryTotal;
    return filtered.map((s, i) => {
      const value = Number(s.count) || 0;
      return {
        key: String(s.type ?? i),
        label: t(`claimCategory_${s.type}`),
        value,
        color: CLAIM_CATEGORY_DOUGHNUT_COLORS[i % CLAIM_CATEGORY_DOUGHNUT_COLORS.length],
        pct: total > 0 ? Math.round((value / total) * 100) : 0,
      };
    });
  }, [claimsByCategoryStats, claimsCategoryTotal, t]);

  const claimsCategoryPieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
        },
      },
    }),
    []
  );

  const claimsStatusTotal = useMemo(() => {
    const s = claimsByStatusStats.series || [];
    return s.reduce((acc, x) => acc + (Number(x.count) || 0), 0);
  }, [claimsByStatusStats]);

  const claimsStatusDonutData = useMemo(() => {
    const series = claimsByStatusStats.series || [];
    const filtered = series.filter((s) => (Number(s.count) || 0) > 0);
    const hasData = filtered.length > 0;
    return {
      labels: hasData
        ? filtered.map((s) => t(`reclamationStatus_${s.status}`))
        : [""],
      datasets: [
        {
          data: hasData ? filtered.map((s) => Number(s.count) || 0) : [1],
          backgroundColor: hasData
            ? filtered.map(
                (s) =>
                  RECLAMATION_STATUS_DONUT_COLORS[s.status] ||
                  RECLAMATION_STATUS_DONUT_COLORS.pending
              )
            : ["#e2e8f0"],
          borderWidth: 0,
        },
      ],
    };
  }, [claimsByStatusStats, t]);

  const claimsStatusDonutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "66%",
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            padding: 14,
            usePointStyle: true,
            pointStyle: "circle",
            font: { size: 12, weight: "500" },
            color: "#334155",
          },
        },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
        },
      },
    }),
    []
  );

  const claimsStatusAreaMax = useMemo(() => {
    const series = claimsByStatusStats.series || [];
    const byStatus = Object.fromEntries(
      series.map((s) => [s.status, Number(s.count) || 0])
    );
    const counts = CLAIMS_STATUS_AXIS_KEYS.filter((k) => claimsStatusAreaVisible[k]).map(
      (k) => byStatus[k] ?? 0
    );
    return Math.max(0, ...counts, 1);
  }, [claimsByStatusStats, claimsStatusAreaVisible]);

  const claimsStatusAreaData = useMemo(() => {
    const series = claimsByStatusStats.series || [];
    const byStatus = Object.fromEntries(
      series.map((s) => [s.status, Number(s.count) || 0])
    );
    const labels = CLAIMS_STATUS_AXIS_KEYS.map((key) => t(`reclamationStatus_${key}`));

    const datasets = CLAIMS_STATUS_AREA_ORDER.filter((statusKey) => claimsStatusAreaVisible[statusKey]).map(
      (statusKey) => {
        const hex =
          RECLAMATION_STATUS_DONUT_COLORS[statusKey] ||
          RECLAMATION_STATUS_DONUT_COLORS.pending;
        const v = byStatus[statusKey] ?? 0;
        const data = CLAIMS_STATUS_AXIS_KEYS.map((k) => (k === statusKey ? v : 0));
        return {
          label: t(`reclamationStatus_${statusKey}`),
          data,
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          borderColor: hex,
          backgroundColor: hexToRgba(hex, 0.38),
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: hex,
          pointBorderColor: "#ffffff",
          pointBorderWidth: 1,
        };
      }
    );

    return { labels, datasets };
  }, [claimsByStatusStats, claimsStatusAreaVisible, t]);

  const claimsStatusAreaOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { top: 12, left: 4, right: 8, bottom: 4 } },
      interaction: { intersect: false, mode: "index" },
      plugins: {
        legend: {
          display: true,
          position: "bottom",
          labels: {
            padding: 12,
            usePointStyle: true,
            pointStyle: "circle",
            font: { size: 11, weight: "500" },
            color: "#334155",
          },
        },
        tooltip: {
          backgroundColor: "rgba(15, 23, 42, 0.92)",
          padding: 12,
          cornerRadius: 10,
        },
      },
      scales: {
        x: {
          grid: { display: false, drawBorder: false },
          ticks: { color: "#64748b", font: { size: 11, weight: "600" } },
          border: { display: false },
        },
        y: {
          beginAtZero: true,
          suggestedMax: Math.max(claimsStatusAreaMax + 1, 4),
          ticks: { precision: 0, color: "#64748b" },
          grid: { color: "rgba(15, 23, 42, 0.06)" },
          border: { display: false },
        },
      },
    }),
    [claimsStatusAreaMax]
  );

  useEffect(() => {
    if (!CLAIMS_CATEGORY_WINDOW_OPTIONS.includes(claimsCategoryDays)) {
      setClaimsCategoryDays(30);
    }
  }, [claimsCategoryDays]);

  const menuLanguageMonthOptions = useMemo(() => {
    const year = new Date().getFullYear();
    const months = getShortMonthLabels(i18n.language);
    return [
      { key: "all", label: t("dashboardFilterAll") },
      ...months.map((label, index) => ({
        key: `${year}-${String(index + 1).padStart(2, "0")}`,
        label,
      })),
    ];
  }, [t, i18n.language]);

  const menuLanguagePeriodHint = useMemo(() => {
    const calendarYear = new Date().getFullYear();
    if (selectedMenuLanguageMonth === "all") {
      return t("menuLanguageClicksPeriodFullYear", { year: calendarYear });
    }
    const match = /^(\d{4})-(\d{2})$/.exec(selectedMenuLanguageMonth);
    if (!match) {
      return t("menuLanguageClicksPeriodFullYear", { year: calendarYear });
    }
    const monthNum = parseInt(match[2], 10);
    const label =
      monthNum >= 1 && monthNum <= 12
        ? getShortMonthLabels(i18n.language)[monthNum - 1]
        : match[2];
    return t("menuLanguageClicksPeriodMonth", { month: label, year: match[1] });
  }, [selectedMenuLanguageMonth, t, i18n.language]);

  return (
    <>
      <div className="dashboard-home" data-tour="tour-page-dashboard">
      <div className="row g-3 dashboard-chart-row">
        <div className="col-12 dash-slot-kpi">
          <div className="dashboard-kpi-grid">
          <div className="card grd-card dashboard-kpi-card dashboard-kpi-card--menus">
            <div className="card-body">
              <div className="kpi-card-layout">
                <div className="kpi-content">
                  <div className="kpi-label">{t("totalActiveMenus")}</div>
                  <h2 className="kpi-value">{totalMenus}</h2>
                </div>
                <div className="kpi-icon-tile">
                  <i className="fa fa-list-ul" />
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card dashboard-section-card">
            <div className="card-header border-0 pb-0 d-flex flex-wrap gap-2 justify-content-between align-items-center">
              <h4 className="text-black fs-20 mb-0 d-flex align-items-center gap-2">
                <LuDatabase
                  className="dashboard-chart-title-icon flex-shrink-0"
                  strokeWidth={1.85}
                  aria-hidden
                />
                {t("realDatabaseStats")}
              </h4>
              <div className="db-stats-filters">
                <div className="db-segmented">
                  {[
                    { key: "all", label: t("dashboardFilterAll") },
                    { key: "menu", label: t("dashboardFilterMenu") },
                    { key: "options", label: t("dashboardFilterOptions") },
                  ].map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={`db-segmented-btn ${
                        dbStatsFilter === f.key ? "is-active" : ""
                      }`}
                      onClick={() => setDbStatsFilter(f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <div className="db-segmented">
                  {[
                    { key: "bar", label: t("dashboardChartBar") },
                    { key: "donut", label: t("dashboardChartDonut") },
                  ].map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      className={`db-segmented-btn ${
                        dbChartType === f.key ? "is-active" : ""
                      }`}
                      onClick={() => setDbChartType(f.key)}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="db-mini-cards">
                {dbFilteredData.map((item) => (
                  <div
                    key={item.key}
                    className={`db-mini-card db-mini-card-${item.key}`}
                  >
                    <div className="db-mini-value">{item.value}</div>
                    <div className="db-mini-label">{item.label}</div>
                  </div>
                ))}
              </div>
              <div className="db-chart-wrap">
                {dbChartType === "donut" ? (
                  <Doughnut data={dbDonutData} options={dbDonutOptions} />
                ) : (
                  <Bar
                    data={dbChartData}
                    options={dbChartOptions}
                    plugins={[barValueLabelPlugin]}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card dashboard-section-card google-review-analytics-card">
            <div className="card-header border-0 pb-0">
              <div className="dashboard-chart-header-shell">
                <div className="dashboard-chart-header-top">
                  <h4 className="text-black fs-20 mb-0 d-flex align-items-center gap-2">
                    <LuMousePointerClick
                      className="dashboard-chart-title-icon flex-shrink-0"
                      strokeWidth={1.85}
                      aria-hidden
                    />
                    {t("googleReviewClicksTitle")}
                  </h4>
                  <div className="dashboard-chart-header-filters">
                    <Nav
                      variant="pills"
                      className="google-review-period-nav flex-wrap gap-1"
                      role="tablist"
                      aria-label={t("googleReviewFilterPeriod")}
                    >
                      {[
                        { key: 7, label: t("googleReviewDays7") },
                        { key: 30, label: t("googleReviewDays30") },
                        { key: 90, label: t("googleReviewDays90") },
                        { key: 365, label: t("googleReviewDays365") },
                      ].map((opt) => (
                        <Nav.Item key={opt.key}>
                          <Nav.Link
                            as="button"
                            type="button"
                            className="google-review-period-pill"
                            active={googleReviewDays === opt.key}
                            onClick={() => setGoogleReviewDays(opt.key)}
                          >
                            {opt.label}
                          </Nav.Link>
                        </Nav.Item>
                      ))}
                    </Nav>
                    <div className="db-segmented" role="group" aria-label={t("googleReviewFilterChart")}>
                      {[
                        { key: "bar", label: t("googleReviewChartBar") },
                        { key: "line", label: t("googleReviewChartLine") },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          className={`db-segmented-btn ${
                            googleReviewChartKind === opt.key ? "is-active" : ""
                          }`}
                          onClick={() => setGoogleReviewChartKind(opt.key)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="fs-13 mb-0 text-muted">
                  {t("googleReviewClicksSubtitle", { days: googleReviewDays })}
                </p>
              </div>
            </div>
            <div className="card-body pt-3">
              <div className="google-review-stat-grid">
                <div className="google-review-stat-card google-review-stat-card--total">
                  <div className="google-review-stat-card-inner">
                    <div
                      className="google-review-stat-icon-wrap google-review-stat-icon-wrap--blue"
                      aria-hidden
                    >
                      <LuMousePointerClick className="google-review-stat-icon" strokeWidth={1.75} />
                    </div>
                    <div className="google-review-stat-text">
                      <div className="google-review-stat-label">{t("googleReviewClicksTotal")}</div>
                      <div className="google-review-stat-value">
                        {googleReviewLoading ? (
                          <Spinner animation="border" size="sm" className="text-primary" />
                        ) : (
                          googleReviewClickStats.total.toLocaleString()
                        )}
                      </div>
                      <div className="google-review-stat-hint">
                        {t("googleReviewClicksSeriesLabel")} · {t("dashboardDaysShort", { count: googleReviewDays })}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="google-review-stat-card google-review-stat-card--avg">
                  <div className="google-review-stat-card-inner">
                    <div
                      className="google-review-stat-icon-wrap google-review-stat-icon-wrap--orange"
                      aria-hidden
                    >
                      <LuHand className="google-review-stat-icon" strokeWidth={1.75} />
                    </div>
                    <div className="google-review-stat-text">
                      <div className="google-review-stat-label">{t("googleReviewStatAvg")}</div>
                      <div className="google-review-stat-value">
                        {googleReviewLoading
                          ? "—"
                          : googleReviewInsights.avg.toFixed(googleReviewInsights.avg < 10 ? 2 : 1)}
                      </div>
                      <div className="google-review-stat-hint">
                        {t("googleReviewStatAvgHint")}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="google-review-stat-card google-review-stat-card--peak">
                  <div className="google-review-stat-card-inner">
                    <div
                      className="google-review-stat-icon-wrap google-review-stat-icon-wrap--green"
                      aria-hidden
                    >
                      <LuCalendarDays className="google-review-stat-icon" strokeWidth={1.75} />
                    </div>
                    <div className="google-review-stat-text">
                      <div className="google-review-stat-label">{t("googleReviewStatPeak")}</div>
                      <div className="google-review-stat-value google-review-stat-value--peak">
                        {googleReviewLoading
                          ? "—"
                          : googleReviewInsights.peak > 0
                            ? `${googleReviewInsights.peakLabel} – ${t("googleReviewStatPeakValue", {
                                count: googleReviewInsights.peak,
                              })}`
                            : "—"}
                      </div>
                      <div className="google-review-stat-hint" aria-hidden>
                        &nbsp;
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {googleReviewClickStats.labels.length === 0 && !googleReviewLoading ? (
                <div className="google-review-empty text-muted py-4 text-center rounded-3 mt-2">
                  {t("googleReviewClicksEmpty")}
                </div>
              ) : (
                <div className="db-chart-wrap google-review-chart-wrap mt-3" style={{ minHeight: 300 }}>
                  {googleReviewChartKind === "line" ? (
                    <Line data={googleReviewLineChartData} options={googleReviewLineChartOptions} />
                  ) : (
                    <Bar
                      data={googleReviewChartData}
                      options={googleReviewChartOptions}
                      plugins={[barValueLabelPlugin]}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card dashboard-section-card google-review-analytics-card">
            <div className="card-header border-0 pb-0">
              <div className="dashboard-chart-header-shell">
                <div className="dashboard-chart-header-top">
                  <h4 className="text-black fs-20 mb-0 d-flex align-items-center gap-2">
                    <LuLanguages
                      className="dashboard-chart-title-icon flex-shrink-0"
                      strokeWidth={1.85}
                      aria-hidden
                    />
                    {t("menuLanguageClicksTitle")}
                  </h4>
                  <div className="dashboard-chart-header-filters db-stats-filters">
                    <div
                      className="db-segmented"
                      role="group"
                      aria-label={t("menuLanguageClicksViewFilterLabel")}
                    >
                      {[
                        { key: "bar", label: t("menuLanguageClicksViewBar") },
                        { key: "pie", label: t("menuLanguageClicksViewPie") },
                        { key: "list", label: t("menuLanguageClicksViewList") },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          className={`db-segmented-btn ${
                            menuLanguageViewKind === opt.key ? "is-active" : ""
                          }`}
                          aria-pressed={menuLanguageViewKind === opt.key}
                          onClick={() => setMenuLanguageViewKind(opt.key)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="fs-13 mb-0 text-muted">
                  {t("menuLanguageClicksSubtitle", {
                    period: menuLanguagePeriodHint,
                  })}
                </p>
                <div className="revenue-month-filter revenue-month-filter-menu-language">
                  <span className="revenue-month-filter-label">
                    {t("menuLanguageClicksMonthFilterLabel")}:
                  </span>
                  <div className="revenue-month-filter-pills" role="tablist">
                    {menuLanguageMonthOptions.map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        role="tab"
                        className={`revenue-month-pill ${
                          selectedMenuLanguageMonth === m.key ? "is-active" : ""
                        }`}
                        aria-selected={selectedMenuLanguageMonth === m.key}
                        onClick={() => setSelectedMenuLanguageMonth(m.key)}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body pt-3">
              <div className="google-review-stat-grid claims-kpi-stat-grid">
                <div className="google-review-stat-card google-review-stat-card--total claims-kpi-card claims-kpi-card--status">
                  <div className="google-review-stat-card-inner">
                    <div
                      className="google-review-stat-icon-wrap claims-kpi-icon-wrap claims-kpi-icon-wrap--status"
                      aria-hidden
                    >
                      <LuLanguages className="google-review-stat-icon" strokeWidth={1.75} />
                    </div>
                    <div className="google-review-stat-text">
                      <div className="google-review-stat-label">{t("menuLanguageClicksTotal")}</div>
                      <div className="google-review-stat-value claims-kpi-value">
                        {menuLanguageClickLoading ? (
                          <Spinner animation="border" size="sm" className="text-primary" />
                        ) : (
                          menuLanguageClickStats.total.toLocaleString()
                        )}
                      </div>
                      <div className="google-review-stat-hint claims-kpi-hint">
                        {t("menuLanguageClicksSeriesLabel")} · {menuLanguagePeriodHint}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {menuLanguageRankedSeries.length === 0 && !menuLanguageClickLoading ? (
                <div className="google-review-empty text-muted py-4 text-center rounded-3 mt-2">
                  {t("menuLanguageClicksEmpty")}
                </div>
              ) : (
                menuLanguageViewKind === "list" ? (
                  <div className="menu-language-top-list mt-3">
                    {menuLanguageRankedSeries.map((row, idx) => {
                      const rank = idx + 1;
                      const color =
                        row.color || MENU_LANGUAGE_RANK_COLORS[idx % MENU_LANGUAGE_RANK_COLORS.length];
                      const pct = menuLanguageClickStats.total
                        ? Math.round((row.count / menuLanguageClickStats.total) * 100)
                        : 0;
                      return (
                        <div
                          key={row.language || row.label}
                          className="menu-language-top-item"
                          style={{ backgroundColor: hexToRgba(color, 0.18) }}
                        >
                          <span className="menu-language-top-rank" title={`#${rank}`}>
                            {rank}
                          </span>
                          <MenuLanguageFlag
                            src={row.flag}
                            alt={row.label}
                            className="menu-language-flag-icon menu-language-flag-icon--list"
                          />
                          <span className="menu-language-top-label">{row.label}</span>
                          <span className="menu-language-top-value">{pct}%</span>
                          <div className="menu-language-hover-card" role="tooltip">
                            <div className="menu-language-hover-card-title">
                              <MenuLanguageFlag
                                src={row.flag}
                                alt=""
                                className="menu-language-flag-icon menu-language-flag-icon--tooltip"
                              />
                              <span>{row.label}</span>
                            </div>
                            <div className="menu-language-hover-card-body">
                              <span
                                className="menu-language-hover-card-swatch"
                                style={{ backgroundColor: hexToRgba(color, 0.38) }}
                                aria-hidden
                              />
                              <span className="menu-language-hover-card-value">
                                {row.count} ({pct}%)
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : menuLanguageViewKind === "pie" ? (
                  <div className="db-chart-wrap google-review-chart-wrap mt-3 claims-category-pie-outer menu-language-pie-outer">
                    <div className="tx-body claims-category-pie-body menu-language-pie-body">
                      <div className="tx-donut-wrap claims-category-pie-wrap menu-language-pie-wrap">
                        <Pie data={menuLanguagePieData} options={menuLanguagePieOptions} />
                      </div>
                      <div className="claims-cat-legend-list menu-language-pie-legend">
                        {menuLanguagePieBreakdown.map((item) => (
                          <div key={item.key} className="claims-cat-legend-row menu-language-pie-legend-row">
                            <MenuLanguageFlag
                              src={item.flag}
                              alt={item.label}
                              className="menu-language-flag-icon menu-language-flag-icon--legend"
                            />
                            <span className="claims-cat-legend-label">{item.label}</span>
                            <div className="claims-cat-legend-track">
                              <div
                                className="claims-cat-legend-fill"
                                style={{
                                  width: `${
                                    item.value > 0 ? Math.max(item.pct, 6) : 0
                                  }%`,
                                  background: item.color,
                                }}
                              />
                            </div>
                            <span className="claims-cat-legend-value">{item.value}</span>
                            <span className="claims-cat-legend-pct" style={{ color: item.color }}>
                              {item.pct}%
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="db-chart-wrap google-review-chart-wrap mt-3 menu-language-bar-wrap" style={{ minHeight: 300 }}>
                    <Bar
                      key={`menu-lang-bar-${menuLanguageFlagEpoch}`}
                      data={menuLanguageRankData}
                      options={menuLanguageRankOptions}
                      plugins={[horizontalBarValueLabelPlugin, menuLanguageBarFlagPlugin]}
                    />
                  </div>
                )
              )}
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card dashboard-section-card google-review-analytics-card claims-by-category-card">
            <div className="card-header border-0 pb-0">
              <div className="dashboard-chart-header-shell">
                <div className="dashboard-chart-header-top">
                  <h4 className="text-black fs-20 mb-0 d-flex align-items-center gap-2">
                    <LuFolderTree
                      className="dashboard-chart-title-icon flex-shrink-0"
                      strokeWidth={1.85}
                      aria-hidden
                    />
                    {t("claimsByCategoryTitle")}
                  </h4>
                  <div className="dashboard-chart-header-filters">
                    <Form.Select
                      size="sm"
                      className="claims-category-window-select ops-chart-select"
                      value={claimsCategoryDays}
                      aria-label={t("claimsCategoryWindowLabel")}
                      onChange={(e) => setClaimsCategoryDays(Number(e.target.value))}
                    >
                      {CLAIMS_CATEGORY_WINDOW_OPTIONS.map((d) => (
                        <option key={d} value={d}>
                          {t("dashboardRollingDaysOption", { count: d })}
                        </option>
                      ))}
                    </Form.Select>
                    <div
                      className="btn-group claims-chart-type-toggle"
                      role="group"
                      aria-label={t("claimsCategoryVizLabel")}
                    >
                      <button
                        type="button"
                        className={`btn btn-outline-secondary claims-chart-type-btn ${
                          claimsCategoryChartKind === "bar" ? "active" : ""
                        }`}
                        title={t("claimsChartHorizontalBar")}
                        aria-pressed={claimsCategoryChartKind === "bar"}
                        onClick={() => setClaimsCategoryChartKind("bar")}
                      >
                        <LuChartBar className="claims-chart-type-icon" strokeWidth={1.75} aria-hidden />
                        <span className="visually-hidden">{t("claimsChartHorizontalBar")}</span>
                      </button>
                      <button
                        type="button"
                        className={`btn btn-outline-secondary claims-chart-type-btn ${
                          claimsCategoryChartKind === "pie" ? "active" : ""
                        }`}
                        title={t("claimsChartPie")}
                        aria-pressed={claimsCategoryChartKind === "pie"}
                        onClick={() => setClaimsCategoryChartKind("pie")}
                      >
                        <LuChartPie className="claims-chart-type-icon" strokeWidth={1.75} aria-hidden />
                        <span className="visually-hidden">{t("claimsChartPie")}</span>
                      </button>
                    </div>
                  </div>
                </div>
                <p className="fs-13 mb-0 text-muted">
                  {t("claimsByCategorySubtitle", { days: claimsCategoryDays })}
                </p>
              </div>
            </div>
            <div className="card-body pt-3">
              <div className="google-review-stat-grid claims-kpi-stat-grid">
                <div className="google-review-stat-card google-review-stat-card--total claims-kpi-card claims-kpi-card--category">
                  <div className="google-review-stat-card-inner">
                    <div
                      className="google-review-stat-icon-wrap claims-kpi-icon-wrap claims-kpi-icon-wrap--category"
                      aria-hidden
                    >
                      <LuMessageSquare className="google-review-stat-icon" strokeWidth={1.75} />
                    </div>
                    <div className="google-review-stat-text">
                      <div className="google-review-stat-label">{t("claimsByCategoryTotal")}</div>
                      <div className="google-review-stat-value claims-kpi-value">
                        {claimsCategoryLoading ? (
                          <Spinner animation="border" size="sm" className="text-primary" />
                        ) : (
                          claimsCategoryTotal.toLocaleString()
                        )}
                      </div>
                      <div className="google-review-stat-hint claims-kpi-hint">
                        {t("claimsByCategorySeriesLabel")} · {t("dashboardDaysShort", { count: claimsCategoryDays })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {claimsCategoryTotal === 0 && !claimsCategoryLoading ? (
                <div className="google-review-empty text-muted py-4 text-center rounded-3 mt-2">
                  {t("claimsByCategoryEmpty")}
                </div>
              ) : (
                <div
                  className={`db-chart-wrap google-review-chart-wrap mt-3 claims-horizontal-bar-wrap${
                    claimsCategoryChartKind === "pie" ? " claims-category-pie-outer" : ""
                  }`}
                  style={
                    claimsCategoryChartKind === "pie"
                      ? undefined
                      : { minHeight: claimsCategoryBarChartMinHeight }
                  }
                >
                  {claimsCategoryChartKind === "pie" ? (
                    <div className="tx-body claims-category-pie-body">
                      <div className="tx-donut-wrap claims-category-pie-wrap">
                        <Pie data={claimsCategoryPieData} options={claimsCategoryPieOptions} />
                      </div>
                      <div className="claims-cat-legend-list">
                        {claimsCategoryPieBreakdown.map((item) => (
                          <div key={item.key} className="claims-cat-legend-row">
                            <span
                              className="claims-cat-legend-dot"
                              style={{ background: item.color }}
                              aria-hidden
                            />
                            <span className="claims-cat-legend-label">{item.label}</span>
                            <div className="claims-cat-legend-track">
                              <div
                                className="claims-cat-legend-fill"
                                style={{
                                  width: `${
                                    item.value > 0 ? Math.max(item.pct, 6) : 0
                                  }%`,
                                  background: item.color,
                                }}
                              />
                            </div>
                            <span className="claims-cat-legend-value">{item.value}</span>
                            <span className="claims-cat-legend-pct" style={{ color: item.color }}>
                              {item.pct}%
                            </span>
                            <div className="tx-line-hover-info">
                              {item.label}: {item.value} ({item.pct}%)
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Bar
                      data={claimsCategoryHorizontalBarData}
                      options={claimsCategoryHorizontalBarOptions}
                      plugins={[horizontalBarValueLabelPlugin]}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-12">
          <div className="card dashboard-section-card google-review-analytics-card reclamation-status-donut-card">
            <div className="card-header border-0 pb-0">
              <div className="dashboard-chart-header-shell">
                <div className="dashboard-chart-header-top">
                  <h4 className="text-black fs-20 mb-0 d-flex align-items-center gap-2">
                    <LuListChecks
                      className="dashboard-chart-title-icon flex-shrink-0"
                      strokeWidth={1.85}
                      aria-hidden
                    />
                    {t("reclamationStatusTitle")}
                  </h4>
                  <div className="dashboard-chart-header-filters">
                    <div
                      className="btn-group claims-chart-type-toggle claims-status-chart-toggle"
                      role="group"
                      aria-label={t("reclamationStatusChartToggleAria")}
                    >
                      <button
                        type="button"
                        className={`btn btn-outline-secondary claims-chart-type-btn ${
                          claimsStatusChartKind === "area" ? "active" : ""
                        }`}
                        title={t("reclamationStatusChartArea")}
                        aria-pressed={claimsStatusChartKind === "area"}
                        onClick={() => setClaimsStatusChartKind("area")}
                      >
                        <LuChartArea className="claims-chart-type-icon" strokeWidth={1.75} aria-hidden />
                        <span className="visually-hidden">{t("reclamationStatusChartArea")}</span>
                      </button>
                      <button
                        type="button"
                        className={`btn btn-outline-secondary claims-chart-type-btn ${
                          claimsStatusChartKind === "doughnut" ? "active" : ""
                        }`}
                        title={t("reclamationStatusChartDonut")}
                        aria-pressed={claimsStatusChartKind === "doughnut"}
                        onClick={() => setClaimsStatusChartKind("doughnut")}
                      >
                        <LuChartPie className="claims-chart-type-icon" strokeWidth={1.75} aria-hidden />
                        <span className="visually-hidden">{t("reclamationStatusChartDonut")}</span>
                      </button>
                    </div>
                  </div>
                </div>
                <p className="fs-13 mb-0 text-muted">
                  {t("reclamationStatusSubtitleLinked", { days: claimsCategoryDays })}
                </p>
              </div>
            </div>
            <div className="card-body pt-3">
              <div className="google-review-stat-grid claims-kpi-stat-grid">
                <div className="google-review-stat-card google-review-stat-card--total claims-kpi-card claims-kpi-card--status">
                  <div className="google-review-stat-card-inner">
                    <div
                      className="google-review-stat-icon-wrap claims-kpi-icon-wrap claims-kpi-icon-wrap--status"
                      aria-hidden
                    >
                      {claimsStatusChartKind === "area" ? (
                        <LuChartArea className="google-review-stat-icon" strokeWidth={1.75} />
                      ) : (
                        <LuChartPie className="google-review-stat-icon" strokeWidth={1.75} />
                      )}
                    </div>
                    <div className="google-review-stat-text">
                      <div className="google-review-stat-label">{t("reclamationStatusTotal")}</div>
                      <div className="google-review-stat-value claims-kpi-value">
                        {claimsStatusLoading ? (
                          <Spinner animation="border" size="sm" className="text-primary" />
                        ) : (
                          claimsStatusTotal.toLocaleString()
                        )}
                      </div>
                      <div className="google-review-stat-hint claims-kpi-hint">
                        {t("reclamationStatusHint")} · {t("dashboardDaysShort", { count: claimsCategoryDays })} ·{" "}
                        {t("reclamationStatusPeriodFromCategory")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {claimsStatusTotal === 0 && !claimsStatusLoading ? (
                <div className="google-review-empty text-muted py-4 text-center rounded-3 mt-2">
                  {t("reclamationStatusEmpty")}
                </div>
              ) : (
                <>
                  {claimsStatusChartKind === "area" ? (
                    <div
                      className="claims-status-area-filter-row d-flex flex-wrap align-items-center gap-2 mt-2"
                      role="group"
                      aria-label={t("reclamationStatusAreaFilterAria")}
                    >
                      <span className="db-filter-label text-muted text-uppercase fw-semibold mb-0">
                        {t("reclamationStatusAreaFilterLabel")}
                      </span>
                      <div className="d-flex flex-wrap gap-2">
                        {CLAIMS_STATUS_AXIS_KEYS.map((statusKey) => {
                          const on = claimsStatusAreaVisible[statusKey];
                          const hex =
                            RECLAMATION_STATUS_DONUT_COLORS[statusKey] ||
                            RECLAMATION_STATUS_DONUT_COLORS.pending;
                          return (
                            <button
                              key={statusKey}
                              type="button"
                              className={`btn btn-sm claims-status-area-filter-btn ${
                                on ? "claims-status-area-filter-btn--on" : "btn-outline-secondary"
                              }`}
                              style={
                                on
                                  ? {
                                      borderColor: hex,
                                      backgroundColor: hexToRgba(hex, 0.18),
                                      color: "#0f172a",
                                    }
                                  : undefined
                              }
                              aria-pressed={on}
                              title={t(`reclamationStatus_${statusKey}`)}
                              onClick={() => toggleClaimsStatusAreaFilter(statusKey)}
                            >
                              {t(`reclamationStatus_${statusKey}`)}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  <div
                    className={`db-chart-wrap google-review-chart-wrap ${
                      claimsStatusChartKind === "area" ? "mt-2" : "mt-3"
                    }${
                      claimsStatusChartKind === "doughnut"
                        ? " reclamation-status-donut-wrap mx-auto"
                        : ""
                    }`}
                    style={
                      claimsStatusChartKind === "area"
                        ? {
                            minHeight:
                              claimsCategoryChartKind === "pie"
                                ? 300
                                : claimsCategoryBarChartMinHeight,
                            maxWidth: "100%",
                          }
                        : { minHeight: 300, maxWidth: 360 }
                    }
                  >
                    {claimsStatusChartKind === "area" ? (
                      <Line data={claimsStatusAreaData} options={claimsStatusAreaOptions} />
                    ) : (
                      <Doughnut data={claimsStatusDonutData} options={claimsStatusDonutOptions} />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <DashboardOpsCharts />

        <div className="col-12 dash-slot-trio">
          <div className="row dashboard-main-trio dashboard-main-trio--no-orders">
            <div className="col-xl-4 col-lg-12 trio-opened">
              <div className="card dashboard-section-card">
                <Tab.Container defaultActiveKey="monthly">
                  <div className="card-header d-sm-flex flex-wrap d-block pb-0 border-0">
                    <div className="me-auto pe-3">
                      <h4 className="text-black fs-20">{t("topOpenedProducts")}</h4>
                      <p className="fs-13 mb-0 text-muted">
                        {t("topOpenedProductsSubtitle")}
                      </p>
                    </div>
                    <div className="card-action card-tabs mt-3 mt-sm-0 mt-3 mb-sm-0 mb-3 mt-sm-0">
                      <Nav as="ul" className="nav nav-tabs" role="tablist">
                        <Nav.Item as="li" className="nav-item">
                          <Nav.Link className="nav-link" eventKey="monthly" role="tab">
                            {t("periodMonthly")}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li" className="nav-item">
                          <Nav.Link className="nav-link" eventKey="weekly" role="tab">
                            {t("periodWeekly")}
                          </Nav.Link>
                        </Nav.Item>
                        <Nav.Item as="li" className="nav-item">
                          <Nav.Link className="nav-link" eventKey="today" role="tab">
                            {t("periodToday")}
                          </Nav.Link>
                        </Nav.Item>
                      </Nav>
                    </div>
                  </div>
                  <div className="card-body">
                    <Tab.Content className="tab-content">
                      <Tab.Pane eventKey="monthly" className="tab-pane fade" id="Monthly">
                        {(openStats.monthly || []).length === 0 ? (
                          <div className="text-muted">{t("noProductOpeningsMonth")}</div>
                        ) : (
                          <div className="d-flex flex-column gap-2">
                            {openStats.monthly.map((p, i) => (
                              <div key={p.productId || i} className="p-3 border rounded d-flex justify-content-between align-items-center">
                                <span className="fw-semibold">{i + 1}. {p.title}</span>
                                <span className="badge bg-light text-dark">
                                  {t("productOpenCount", { count: p.openCount })}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Tab.Pane>
                      <Tab.Pane eventKey="weekly" className="tab-pane fade" id="Weekly">
                        {(openStats.weekly || []).length === 0 ? (
                          <div className="text-muted">{t("noProductOpeningsWeek")}</div>
                        ) : (
                          <div className="d-flex flex-column gap-2">
                            {openStats.weekly.map((p, i) => (
                              <div key={p.productId || i} className="p-3 border rounded d-flex justify-content-between align-items-center">
                                <span className="fw-semibold">{i + 1}. {p.title}</span>
                                <span className="badge bg-light text-dark">
                                  {t("productOpenCount", { count: p.openCount })}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Tab.Pane>
                      <Tab.Pane eventKey="today" className="tab-pane fade" id="Today">
                        {(openStats.today || []).length === 0 ? (
                          <div className="text-muted">{t("noProductOpeningsToday")}</div>
                        ) : (
                          <div className="d-flex flex-column gap-2">
                            {openStats.today.map((p, i) => (
                              <div key={p.productId || i} className="p-3 border rounded d-flex justify-content-between align-items-center">
                                <span className="fw-semibold">{i + 1}. {p.title}</span>
                                <span className="badge bg-light text-dark">
                                  {t("productOpenCount", { count: p.openCount })}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Tab.Pane>
                    </Tab.Content>
                  </div>
                </Tab.Container>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 dash-slot-storage">
          <div className="card dashboard-section-card image-storage-compact">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <div className="kpi-label mb-0">{t("dashboardImageStorage")}</div>
                <div className="fw-semibold ms-auto" style={{ minWidth: "72px", textAlign: "right" }}>
                  {imageStoragePercent.toFixed(1)}%
                </div>
                <div className="kpi-icon-tile ms-2">
                  <i className="fa fa-database" />
                </div>
              </div>
              <h4 className="mb-2">{imageStorageUsage}</h4>
              <div className="image-storage-track">
                <div className="image-storage-fill" style={{ width: `${imageStoragePercent}%` }} />
              </div>
              <div className="mt-2 image-storage-hint">
                {t("dashboardImageStorageUsedHint")}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}

export default Home;
